import React, { useState, useEffect, useRef } from "react";
import html2pdf from "html2pdf.js";
import ReactECharts from "echarts-for-react";
import {
  Box, Typography, Grid, Card, CardContent, CardActions,
  Button, Dialog, DialogTitle, DialogContent, IconButton,
  Avatar, Table, TableBody, TableCell, TableHead,
  TableRow, TableContainer, CircularProgress,
  Menu, MenuItem, Checkbox, Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import axiosInstance from "../../../services/axiosInterceptors";
import apiEndPointsConfig from "../../../services/apiEndPoints";
import CommonFilter from "../../../components/CommonFilter";

// Hides the "Improvement Report" card button (and collapses the card's
// action row back to 2 columns) while the report's charts are still being
// refined - the report itself (ImprovementReportContent, reportMode
// "improvement") stays fully implemented and reachable by flipping this back.
const SHOW_IMPROVEMENT_REPORT_BUTTON = false;

/* ─── helpers ─────────────────────────────────────────────────── */
function getInitials(name = "") {
  return name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
}

function getAvatarColor(name = "") {
  const colors = ["#7c3aed", "#0a936f", "#1f6aa5", "#d98200", "#d92d20", "#4f46e5"];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  return colors[Math.abs(h) % colors.length];
}

function fmtDate(iso = "") {
  if (!iso) return "—";
  const parts = iso.split("-");
  if (parts.length < 3) return iso;
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${+parts[2]} ${months[+parts[1] - 1]}`;
}

function fmtDateSlash(iso = "") {
  const datePart = String(iso).slice(0, 10);
  const parts = datePart.split("-");
  if (parts.length < 3) return datePart;
  const [year, month, day] = parts;
  return `${day}/${month}/${year}`;
}

function deriveStats(agent) {
  const activeTrend = (agent.trend || []).filter((d) => (d.totalCalls || 0) > 0);
  const scores = activeTrend.map((d) => d.averageCallScore || 0);
  return {
    latestScore: scores[scores.length - 1] ?? 0,
    baselineScore: scores[0] ?? 0,
    movement: (scores[scores.length - 1] ?? 0) - (scores[0] ?? 0),
    bestScore: scores.length ? Math.max(...scores) : 0,
    lowestScore: scores.length ? Math.min(...scores) : 0,
    activeTrend,
  };
}

/* ─── auto-compute data-driven findings ───────────────────────── */
function computeAutoFindings(paramData, activeTrend) {
  if (!paramData?.length || !activeTrend?.length) return null;
  const baseline = activeTrend[0];
  const latest = activeTrend[activeTrend.length - 1];
  const improvements = [];
  const holdingSteady = [];
  const targetOptimization = [];

  paramData.forEach((p) => {
    const bEntry = (p.trend || []).find((t) => t.date === baseline.date);
    const lEntry = (p.trend || []).find((t) => t.date === latest.date);
    const bScore = bEntry && (bEntry.totalCalls || 0) > 0 ? bEntry.averageCallScore || 0 : null;
    const lScore = lEntry && (lEntry.totalCalls || 0) > 0 ? lEntry.averageCallScore || 0 : null;
    const avg = +(p.averageCallScore || 0).toFixed(2);

    if (bScore === null && lScore === null) return;
    const delta = lScore !== null && bScore !== null ? lScore - bScore : null;

    if (delta !== null && delta > 0.5) {
      improvements.push(`${p.parameter} improved by +${delta.toFixed(1)}% from baseline (${bScore.toFixed(1)}% → ${lScore.toFixed(1)}%).`);
    } else if (delta !== null && Math.abs(delta) <= 0.5) {
      holdingSteady.push(`${p.parameter} is holding steady near baseline (avg: ${avg}%).`);
    } else if (delta !== null && delta < -0.5) {
      targetOptimization.push(`${p.parameter} declined by ${Math.abs(delta).toFixed(1)}% from baseline (${bScore.toFixed(1)}% → ${lScore.toFixed(1)}%). Average across period: ${avg}%.`);
    } else if (avg < 30) {
      targetOptimization.push(`${p.parameter} is critically low at ${avg}% average. Requires immediate attention.`);
    }
  });

  // overall summary
  const baseTotal = activeTrend[0]?.averageCallScore || 0;
  const latestTotal = activeTrend[activeTrend.length - 1]?.averageCallScore || 0;
  if (latestTotal < baseTotal - 1) {
    targetOptimization.unshift(
      `The agent's average score for the period is ${(paramData.reduce((s, p) => s + (p.averageCallScore || 0), 0) / paramData.length).toFixed(2)}%, showing a significant decline from the baseline score of ${baseTotal.toFixed(2)}%, with the latest score dropping further to ${latestTotal.toFixed(2)}%.`
    );
  }

  return { improvements, holdingSteady, targetOptimization };
}

/* ─── parse LLM insights ──────────────────────────────────────── */
function parseInsights(data) {
  if (!data) return null;

  // New structured format
  if (data.improvements !== undefined || data.holdingSteady !== undefined || data.targetOptimization !== undefined) {
    const priorityActions = (data.priorityActions || data.priorityActionPlan || []).map((a, i) => {
      if (typeof a === "string") {
        return { focusArea: `Area ${i + 1}`, actionRequired: a, priority: i < 3 ? "Immediate" : "This Week" };
      }
      return { focusArea: a.focusArea || `Area ${i + 1}`, actionRequired: a.actionRequired || a.action || "", scoreImpact: a.scoreImpact, priority: a.priority || (i < 3 ? "Immediate" : "This Week") };
    });
    return {
      improvements: data.improvements || [],
      holdingSteady: data.holdingSteady || [],
      targetOptimization: data.targetOptimization || data.targetOptimizationAreas || [],
      priorityActions,
    };
  }

  // Old flat format
  if (data.keyFindings || data.priorityActionPlan) {
    const findings = Array.isArray(data.keyFindings) ? data.keyFindings : [];
    const improvements = findings.filter((f) => /improv|increas|better|gain|strength|high(?:est)?/i.test(f));
    const holdingSteady = findings.filter((f) => /hold|steady|maintain|stable|consistent|comparable|similar/i.test(f) && !improvements.includes(f));
    const targetOptimization = findings.filter((f) => !improvements.includes(f) && !holdingSteady.includes(f));
    const priorityActions = (Array.isArray(data.priorityActionPlan) ? data.priorityActionPlan : []).map((a, i) => {
      if (typeof a === "string") return { focusArea: `Area ${i + 1}`, actionRequired: a, priority: i < 3 ? "Immediate" : "This Week" };
      return { focusArea: a.focusArea || `Area ${i + 1}`, actionRequired: a.actionRequired || a.action || String(a), priority: a.priority || (i < 3 ? "Immediate" : "This Week") };
    });
    return { improvements, holdingSteady, targetOptimization, priorityActions };
  }

  return null;
}

/* ─── merge LLM + auto findings ──────────────────────────────── */
function mergeFindings(parsed, auto) {
  if (!parsed && !auto) return null;
  if (!parsed) return { ...auto, priorityActions: [] };
  if (!auto) return parsed;
  const dedup = (llm, autArr) => {
    const seen = new Set(llm.map((s) => s.slice(0, 30)));
    return [...llm, ...autArr.filter((s) => !seen.has(s.slice(0, 30)))];
  };
  return {
    improvements: dedup(parsed.improvements, auto.improvements),
    holdingSteady: dedup(parsed.holdingSteady, auto.holdingSteady),
    targetOptimization: dedup(parsed.targetOptimization, auto.targetOptimization),
    priorityActions: parsed.priorityActions || [],
  };
}

function getOverallCompetencyTag(score = 0) {
  if (score >= 85) return "Expert";
  if (score >= 70) return "Proficient";
  if (score >= 50) return "Developing";
  return "Under Developed";
}

const PARAMETER_SEQUENCE = [
  "OPENING & CREDIBILITY",
  "FAMILY PROFILING - CRITICAL FOUNDATION",
  "HOUSEHOLD INCOME ANALYSIS - CRITICAL",
  "EXPENSE MAPPING - COMPLETE BREAKDOWN",
  "ASSET DISCOVERY & NET WORTH",
  "GOAL-BASED PLANNING - CRITICAL",
  "RISK PROFILING & PORTFOLIO REVIEW",
  "PROTECTION GAP ANALYSIS",
  "SOLUTION PRESENTATION",
  "OBJECTION HANDLING - MDRT PROFESSIONAL",
  "CLOSING & COMMITMENT",
  "REFERRALS & LONG-TERM RELATIONSHIP",
  "MEETING QUALITY & PROFESSIONALISM"
];

function normalizeParameterName(name = "") {
  return String(name).trim().replace(/\s+/g, " ").toUpperCase();
}

function getParameterOrder(name = "") {
  const normalized = normalizeParameterName(name);
  const index = PARAMETER_SEQUENCE.findIndex((param) => normalizeParameterName(param) === normalized);
  return index === -1 ? PARAMETER_SEQUENCE.length : index;
}

function sortByParameterSequence(a, b) {
  const aName = a.parameter || a.category || "";
  const bName = b.parameter || b.category || "";
  const orderDelta = getParameterOrder(aName) - getParameterOrder(bName);
  if (orderDelta !== 0) return orderDelta;
  return normalizeParameterName(aName).localeCompare(normalizeParameterName(bName));
}

function getGeneralBehaviourNote(row) {
  if (row.applicability === "Not Applicable") {
    return "This parameter was generally not relevant in the selected-period conversations, so it should not negatively impact the competency view.";
  }

  const level = row.maturityLevel || "Developing";
  const parameter = String(row.parameter || "this parameter").toLowerCase();

  if (level === "Expert") {
    return `The branch manager generally demonstrates strong ${parameter} behaviour, including confident probing, clear direction, and effective handling of customer responses.`;
  }
  if (level === "Proficient") {
    return `The branch manager generally demonstrates adequate ${parameter} behaviour, with relevant information captured and the conversation handled with reasonable structure.`;
  }
  if (level === "Developing") {
    return `The branch manager shows basic ${parameter} behaviour, but the general pattern indicates more detailed probing, profiling, or follow-through is required.`;
  }
  return `The branch manager generally needs to strengthen ${parameter}; the selected-period pattern indicates the competency is not being demonstrated consistently enough.`;
}

function MaturityChip({ level }) {
  const styles = {
    Expert: { bg: "#dcfce7", fg: "#166534" },
    Proficient: { bg: "#dbeafe", fg: "#1e40af" },
    Developing: { bg: "#fef3c7", fg: "#92400e" },
    "Under Developed": { bg: "#fee2e2", fg: "#991b1b" },
    "Not Applicable": { bg: "#f1f5f9", fg: "#64748b" },
  };
  const s = styles[level] || styles.Developing;
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: 96,
        minHeight: 22,
        px: 1,
        py: 0.25,
        bgcolor: s.bg,
        color: `${s.fg} !important`,
        borderRadius: 1,
        fontWeight: 900,
        fontSize: 10.5,
        lineHeight: 1.35,
        whiteSpace: "nowrap",
        WebkitTextFillColor: s.fg,
      }}
    >
      {level || "Developing"}
    </Box>
  );
}

function SummaryMetric({ label, value, helper }) {
  return (
    <Box sx={{ border: "1px solid #dde3eb", bgcolor: "#f8fafc", borderRadius: 1, p: 1.5 }}>
      <Typography fontSize={10.5} color="#657282" fontWeight={800} textTransform="uppercase" letterSpacing="0.06em">
        {label}
      </Typography>
      <Typography fontSize={20} fontWeight={900} color="#202832" lineHeight={1.25}>{value}</Typography>
      {helper && <Typography fontSize={10.5} color="#64748b" mt={0.25}>{helper}</Typography>}
    </Box>
  );
}

function TextList({ items, emptyText = "No items identified.", color = "#374151" }) {
  const cleanItems = (items || []).filter(Boolean).slice(0, 6);
  if (!cleanItems.length) return <Typography fontSize={11} color="#9ca3af" fontStyle="italic">{emptyText}</Typography>;
  return cleanItems.map((item, i) => (
    <Box key={i} display="flex" gap={0.75} mb={0.8} sx={{ pageBreakInside: "avoid" }}>
      <Typography fontSize={11} color={color} fontWeight={900} sx={{ flexShrink: 0 }}>{i + 1}.</Typography>
      <Typography fontSize={11} color="#374151" lineHeight={1.6}>
        {typeof item === "string" ? item : item.reason || item.parameter || item.description || ""}
      </Typography>
    </Box>
  ));
}

function getParameterRows(summary) {
  return (summary?.categories || []).map((cat) => {
    const isApplicable = (cat.applicableCount || 0) > 0;
    return {
      parameter: cat.category,
      applicability: isApplicable ? "Applicable" : "Not Applicable",
      maturityLevel: cat.dominantMaturity || (isApplicable ? "Developing" : "Not Applicable"),
      averageRating: cat.averageRating,
      evidence:
        cat.developmentAreas?.[0] ||
        cat.gaps?.[0] ||
        cat.strengths?.[0] ||
        cat.description ||
        "No evidence note available.",
    };
  }).sort(sortByParameterSequence);
}

function getParameterMaturityStats(summary) {
  const levels = ["Expert", "Proficient", "Developing", "Under Developed"];
  const rows = getParameterRows(summary).filter((row) => row.applicability === "Applicable");
  const distribution = levels.reduce((acc, level) => ({ ...acc, [level]: 0 }), {});
  rows.forEach((row) => {
    const level = levels.includes(row.maturityLevel) ? row.maturityLevel : "Developing";
    distribution[level] += 1;
  });
  return { levels, distribution, totalApplicable: Math.max(1, rows.length) };
}

function SkillsCompetencySummary({ summary }) {
  if (!summary) return null;
  const parameterRows = getParameterRows(summary);
  const applicable = parameterRows.filter((row) => row.applicability === "Applicable").length;
  const notApplicable = parameterRows.length - applicable;
  const total = parameterRows.length;
  const applicabilityRate = total ? ((applicable / total) * 100).toFixed(1) : "0.0";
  const strengths = parameterRows
    .filter((row) => ["Expert", "Proficient"].includes(row.maturityLevel))
    .map((row) => `${row.parameter}: ${row.evidence}`);
  const gaps = parameterRows
    .filter((row) => ["Under Developed", "Developing"].includes(row.maturityLevel))
    .map((row) => `${row.parameter}: ${row.evidence}`);
  const developmentAreas = parameterRows
    .filter((row) => row.maturityLevel !== "Expert")
    .map((row) => `${row.parameter}: ${row.evidence}`);

  return (
    <Box>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 1.25, mb: 2 }}>
        <SummaryMetric label="Calls In Period" value={summary.totalCalls || 0} helper="Selected report period" />
        <SummaryMetric label="Applicable Parameters" value={applicable} helper={`${applicabilityRate}% evaluated`} />
        <SummaryMetric label="N/A Parameters" value={notApplicable} helper="Excluded from scoring" />
        <SummaryMetric label="Resistance Cases" value={summary.customerResistanceInstances || 0} helper={`${summary.sellerAttemptedUnderResistance || 0} seller attempts`} />
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", border: "1px solid #dde3eb", borderRadius: 1, overflow: "hidden" }}>
        {[
          { title: "Strengths", items: strengths, bg: "#f0fdf4", fg: "#166534" },
          { title: "Gaps", items: gaps, bg: "#fff7ed", fg: "#9a3412" },
          { title: "Development Areas", items: developmentAreas, bg: "#fff1f2", fg: "#991b1b" },
        ].map((col, i) => (
          <Box key={col.title} sx={{ bgcolor: col.bg, p: 1.5, borderRight: i < 2 ? "1px solid #dde3eb" : "none" }}>
            <Typography fontWeight={900} fontSize={11} color={col.fg} textTransform="uppercase" letterSpacing="0.05em" mb={1}>
              {col.title}
            </Typography>
            <TextList items={col.items} color={col.fg} />
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function MaturityDistributionSection({ summary }) {
  if (!summary) return null;
  const { levels, distribution, totalApplicable } = getParameterMaturityStats(summary);
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 1.25 }}>
      {levels.map((level) => {
        const count = distribution[level] || 0;
        const pct = ((count / totalApplicable) * 100).toFixed(1);
        return (
          <Box key={level} sx={{ border: "1px solid #dde3eb", borderRadius: 1, p: 1.5, bgcolor: "#fff" }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
              <MaturityChip level={level} />
              <Typography fontWeight={900} fontSize={18} color="#202832">{count}</Typography>
            </Box>
            <Box sx={{ height: 7, bgcolor: "#e5e7eb", borderRadius: 1, mt: 1.25, overflow: "hidden" }}>
              <Box sx={{
                height: "100%",
                width: `${pct}%`,
                bgcolor: level === "Expert" ? "#16a34a" : level === "Proficient" ? "#2563eb" : level === "Developing" ? "#d97706" : "#dc2626"
              }} />
            </Box>
            <Typography fontSize={10.5} color="#64748b" mt={0.75}>{pct}% of applicable parameters</Typography>
          </Box>
        );
      })}
    </Box>
  );
}

function ContextEvaluationSection({ summary }) {
  if (!summary) return null;
  const rows = getParameterRows(summary);
  return (
    <TableContainer sx={{ border: "1px solid #dde3eb", borderRadius: 1, overflow: "hidden" }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: "#2d3748" }}>
            {["Competency Area", "Dominant Level", "Applicability", "Avg Rating", "General Behaviour"].map((h) => (
              <TableCell key={h} sx={{ color: "#fff", fontWeight: 800, fontSize: 11 }}>{h}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i) => (
            <TableRow key={row.parameter} sx={{ bgcolor: i % 2 === 0 ? "#fff" : "#f8fafc" }}>
              <TableCell sx={{ fontSize: 11, fontWeight: 800, color: "#374151" }}>{row.parameter}</TableCell>
              <TableCell><MaturityChip level={row.maturityLevel} /></TableCell>
              <TableCell sx={{ fontSize: 11, fontWeight: 700 }}>{row.applicability}</TableCell>
              <TableCell sx={{ fontSize: 11, fontWeight: 800 }}>{row.averageRating ? `${row.averageRating}/5` : "N/A"}</TableCell>
              <TableCell sx={{ fontSize: 11, lineHeight: 1.55, color: "#4b5563" }}>
                {getGeneralBehaviourNote(row)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// Replaces the plain "Total Calls" SummaryMetric tile with a clickable one.
// The checklist (fullCallList) always lists every call in the period across
// BOTH New Call/Follow Up types - one shared selection regardless of which
// tab is active - while the displayed count is derived from tabCallList
// (scoped to whichever tab is currently open).
// Checkbox changes are staged in local `pendingSelection` and only applied
// (triggering the parent's refetch) when "OK" is clicked - otherwise every
// checkbox click would immediately refetch the report and swap Section 1 to
// a loading spinner mid-click, unmounting this menu out from under the user.
// While the menu is open, the tile previews the PENDING count rather than
// the last-applied one - it used to keep showing the stale applied count
// while the checklist underneath already had different boxes ticked.
function CallSelectorTile({ tabCallList = [], fullCallList = [], selectedCallIds, onChangeSelectedCallIds }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [pendingSelection, setPendingSelection] = useState(selectedCallIds);
  const isPendingAll = pendingSelection === null;
  const isAppliedAll = selectedCallIds === null;
  const isOpen = Boolean(anchorEl);

  const handleOpen = (e) => {
    if (!fullCallList.length) return;
    setPendingSelection(selectedCallIds);
    setAnchorEl(e.currentTarget);
  };

  // Dismissing without clicking OK (Cancel, backdrop click, Escape) discards
  // whatever was checked/unchecked since the menu opened.
  const handleDiscard = () => {
    setPendingSelection(selectedCallIds);
    setAnchorEl(null);
  };

  const handleApply = () => {
    onChangeSelectedCallIds(pendingSelection);
    setAnchorEl(null);
  };

  const toggleAll = () => {
    setPendingSelection(isPendingAll ? [] : null);
  };

  const toggleCall = (audioId) => {
    const current = isPendingAll ? fullCallList.map((c) => c.audioId) : pendingSelection;
    const next = current.includes(audioId) ? current.filter((id) => id !== audioId) : [...current, audioId];
    // Checking every individual call collapses back to the "All" sentinel.
    setPendingSelection(next.length === fullCallList.length ? null : next);
  };

  const tabTotal = tabCallList.length;
  const appliedTabCount = isAppliedAll
    ? tabTotal
    : tabCallList.filter((c) => selectedCallIds.includes(c.audioId)).length;
  const pendingTabCount = isPendingAll
    ? tabTotal
    : tabCallList.filter((c) => pendingSelection.includes(c.audioId)).length;
  const pendingCount = isPendingAll ? fullCallList.length : pendingSelection.length;

  // While the menu is open, preview the PENDING (unsaved) selection instead
  // of the last-applied one - otherwise the tile kept showing the stale
  // applied count while the checklist underneath already showed different
  // checkboxes ticked, reading as if the click hadn't registered.
  const displayIsAll = isOpen ? isPendingAll : isAppliedAll;
  const displayTabCount = isOpen ? pendingTabCount : appliedTabCount;
  const valueLabel = !fullCallList.length
    ? String(tabTotal)
    : displayIsAll
    ? `All (${tabTotal})`
    : `${displayTabCount} of ${tabTotal}`;

  return (
    <>
    <Box
      data-html2canvas-ignore="true"
      onClick={handleOpen}
      sx={{
        border: "1px solid #dde3eb", bgcolor: "#f8fafc", borderRadius: 1, p: 1.5,
        cursor: fullCallList.length > 0 ? "pointer" : "default",
        "&:hover": fullCallList.length > 0 ? { borderColor: "#1e40af", bgcolor: "#eff6ff" } : undefined,
      }}
    >
      <Typography fontSize={10.5} color="#657282" fontWeight={800} textTransform="uppercase" letterSpacing="0.06em">
        Total Calls
      </Typography>
      <Box display="flex" alignItems="center" gap={0.25}>
        <Typography fontSize={20} fontWeight={900} color="#202832" lineHeight={1.25}>{valueLabel}</Typography>
        {fullCallList.length > 0 && <KeyboardArrowDownIcon sx={{ fontSize: 18, color: "#657282" }} />}
      </Box>
      <Typography fontSize={10.5} color="#64748b" mt={0.25}>Selected report period</Typography>
    </Box>

      {/* Rendered as a SIBLING of the tile Box above, not a child - Menu's
          content is portaled to document.body, but React's synthetic events
          still bubble along the component tree. Nesting it inside the tile's
          onClick={handleOpen} Box meant every click inside the menu (a
          checkbox, Cancel, OK) also re-triggered handleOpen afterwards,
          which reset pendingSelection back to the applied selection and made
          every checkbox toggle appear to silently revert. */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleDiscard} PaperProps={{ sx: { width: 260 } }}>
        <MenuItem dense onClick={toggleAll} sx={{ py: 0.5 }}>
          <Checkbox size="small" checked={isPendingAll} sx={{ p: 0.5, mr: 0.5 }} />
          <Typography fontSize={12.5} fontWeight={700}>All ({fullCallList.length})</Typography>
        </MenuItem>
        <Divider />
        {/* Capped to ~5 visible rows (5 x 40px) - scrolls for anything beyond that. */}
        <Box sx={{ maxHeight: 200, overflowY: "auto" }}>
          {fullCallList.map((call) => {
            const checked = isPendingAll || pendingSelection.includes(call.audioId);
            return (
              <MenuItem key={call.audioId} dense onClick={() => toggleCall(call.audioId)} sx={{ py: 0.5 }}>
                <Checkbox size="small" checked={checked} sx={{ p: 0.5, mr: 0.5 }} />
                <Typography fontSize={12.5}>Call {call.sequence} - {fmtDateSlash(call.callDate)}</Typography>
              </MenuItem>
            );
          })}
        </Box>
        <Divider />
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 1.5, py: 1 }}>
          <Typography fontSize={11} color="#657282">{pendingCount} selected</Typography>
          <Box display="flex" gap={1}>
            <Button size="small" onClick={handleDiscard} sx={{ textTransform: "none", fontSize: 12, minWidth: 0 }}>
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={handleApply}
              sx={{ textTransform: "none", fontSize: 12, minWidth: 0, bgcolor: "#1e40af", "&:hover": { bgcolor: "#1e3a8a" } }}
            >
              OK
            </Button>
          </Box>
        </Box>
      </Menu>
    </>
  );
}

function OnePageSummarySection({ summary, tabCallList, fullCallList, selectedCallIds, onChangeSelectedCallIds }) {
  const overallScore = summary?.overallScore || 0;
  const tag = getOverallCompetencyTag(overallScore);

  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1.25, mb: 2 }}>
      <Box sx={{ border: "1px solid #dde3eb", bgcolor: "#f8fafc", borderRadius: 1, p: 1.5 }}>
        <Typography fontSize={10.5} color="#657282" fontWeight={800} textTransform="uppercase" letterSpacing="0.06em">
          Overall Competency Tag
        </Typography>
        <Box mt={0.75}>
          <MaturityChip level={tag} />
        </Box>
        <Typography fontSize={10.5} color="#64748b" mt={0.75}>
          Period avg: {overallScore.toFixed(1)}%
        </Typography>
      </Box>
      <CallSelectorTile
        tabCallList={tabCallList}
        fullCallList={fullCallList}
        selectedCallIds={selectedCallIds}
        onChangeSelectedCallIds={onChangeSelectedCallIds}
      />
      <SummaryMetric label="Score" value={`${overallScore.toFixed(1)}%`} helper="Overall score" />
    </Box>
  );
}

/* ─── New Call / Follow Up Call toggle for the One Page Report ──── */
function CallTypeToggle({ value, onChange, counts }) {
  const options = [
    { key: "newCall", label: "New Calls" },
    { key: "followUp", label: "Follow Up Calls" },
  ];
  return (
    <Box
      data-html2canvas-ignore="true"
      sx={{ display: "inline-flex", border: "1px solid #dde3eb", borderRadius: 999, p: 0.4, bgcolor: "#f1f5f9" }}
    >
      {options.map((opt) => {
        const active = value === opt.key;
        const count = counts?.[opt.key];
        return (
          <Box
            key={opt.key}
            component="button"
            type="button"
            onClick={() => onChange(opt.key)}
            sx={{
              border: "none",
              cursor: "pointer",
              px: 2,
              py: 0.6,
              borderRadius: 999,
              fontWeight: 800,
              fontSize: 11.5,
              letterSpacing: "0.02em",
              bgcolor: active ? "#2d3748" : "transparent",
              color: active ? "#fff" : "#4b5563",
              transition: "background-color 0.15s, color 0.15s",
            }}
          >
            {opt.label}
            {typeof count === "number" ? ` (${count})` : ""}
          </Box>
        );
      })}
    </Box>
  );
}

function OnePageMaturitySection({ summary }) {
  const categories = getParameterRows(summary);
  if (!categories.length) return <Typography fontSize={13} color="#9ca3af">No maturity data available.</Typography>;
  const levels = ["Expert", "Proficient", "Developing", "Under Developed"];
  const parameterDistribution = categories.reduce((acc, cat) => {
    const level = levels.includes(cat.maturityLevel) ? cat.maturityLevel : "Developing";
    acc[level] = (acc[level] || 0) + 1;
    return acc;
  }, {});
  const totalParameters = Math.max(1, categories.length);
  const levelColors = {
    Expert: { bar: "#16a34a", bg: "#dcfce7", fg: "#166534" },
    Proficient: { bar: "#2563eb", bg: "#dbeafe", fg: "#1e40af" },
    Developing: { bar: "#d97706", bg: "#fef3c7", fg: "#92400e" },
    "Under Developed": { bar: "#dc2626", bg: "#fee2e2", fg: "#991b1b" },
  };

  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 1.25 }}>
      {levels.map((level) => {
        const count = parameterDistribution[level] || 0;
        const pct = ((count / totalParameters) * 100).toFixed(1);
        const taggedParams = categories.filter((cat) => cat.maturityLevel === level);
        const colors = levelColors[level];
        return (
          <Box key={level} sx={{ border: "1px solid #dde3eb", borderRadius: 1, p: 1.5, bgcolor: "#fff", pageBreakInside: "avoid" }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
              <Box sx={{ bgcolor: colors.bg, color: colors.fg, fontWeight: 900, fontSize: 11, borderRadius: 1, px: 1.5, py: 0.75, minWidth: 112, textAlign: "center" }}>
                {level}
              </Box>
              <Typography fontWeight={900} fontSize={20} color="#202832">{count}</Typography>
            </Box>
            <Box sx={{ height: 7, bgcolor: "#e5e7eb", borderRadius: 1, mt: 1.25, overflow: "hidden" }}>
              <Box sx={{ height: "100%", width: `${pct}%`, bgcolor: colors.bar }} />
            </Box>
            <Typography fontSize={10.5} color="#405577" mt={0.75}>{pct}% of evaluated parameters</Typography>
            <Box sx={{ mt: 1.25, pt: 1, borderTop: "1px solid #eef2f7" }}>
              {taggedParams.length ? (
                taggedParams.map((cat) => (
                  <Typography key={cat.parameter} fontSize={10.5} color="#374151" lineHeight={1.45} fontWeight={700} mb={0.6}>
                    {cat.parameter}
                  </Typography>
                ))
              ) : (
                <Typography fontSize={10.5} color="#9ca3af" fontStyle="italic">No parameters tagged</Typography>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

// New view: buckets each parameter by how its rating has trended since the
// agent's baseline (first-ever graded call) instead of by its rating in the
// selected period. Data comes from apiEndPointsConfig.agentCompetencyImprovement,
// fetched separately in OnePageReportContent - see improvementSummary there.
function OnePageImprovementSection({ improvementSummary, loading }) {
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress size={28} sx={{ color: "#1e40af" }} />
      </Box>
    );
  }

  const categories = improvementSummary?.categories || [];
  if (!categories.length) {
    return <Typography fontSize={13} color="#9ca3af">No improvement data available.</Typography>;
  }

  const scoredCategories = categories.filter((cat) => cat.status !== "Insufficient Data");
  const insufficientCategories = categories.filter((cat) => cat.status === "Insufficient Data");
  const levels = ["Strongly Improved", "Improved", "Stable", "Declined"];
  const levelColors = {
    "Strongly Improved": { bar: "#16a34a", bg: "#dcfce7", fg: "#166534" },
    Improved: { bar: "#2563eb", bg: "#dbeafe", fg: "#1e40af" },
    Stable: { bar: "#d97706", bg: "#fef3c7", fg: "#92400e" },
    Declined: { bar: "#dc2626", bg: "#fee2e2", fg: "#991b1b" },
  };
  const totalScored = Math.max(1, scoredCategories.length);

  return (
    <Box>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 1.25 }}>
        {levels.map((level) => {
          const tagged = scoredCategories.filter((cat) => cat.status === level);
          const count = tagged.length;
          const pct = ((count / totalScored) * 100).toFixed(1);
          const colors = levelColors[level];
          return (
            <Box key={level} sx={{ border: "1px solid #dde3eb", borderRadius: 1, p: 1.5, bgcolor: "#fff", pageBreakInside: "avoid" }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
                <Box sx={{ bgcolor: colors.bg, color: colors.fg, fontWeight: 900, fontSize: 11, borderRadius: 1, px: 1.5, py: 0.75, minWidth: 112, textAlign: "center" }}>
                  {level}
                </Box>
                <Typography fontWeight={900} fontSize={20} color="#202832">{count}</Typography>
              </Box>
              <Box sx={{ height: 7, bgcolor: "#e5e7eb", borderRadius: 1, mt: 1.25, overflow: "hidden" }}>
                <Box sx={{ height: "100%", width: `${pct}%`, bgcolor: colors.bar }} />
              </Box>
              <Typography fontSize={10.5} color="#405577" mt={0.75}>{pct}% of evaluated parameters</Typography>
              <Box sx={{ mt: 1.25, pt: 1, borderTop: "1px solid #eef2f7" }}>
                {tagged.length ? (
                  tagged.map((cat) => (
                    <Typography key={cat.category} fontSize={10.5} color="#374151" lineHeight={1.45} fontWeight={700} mb={0.6}>
                      {cat.category}
                      {typeof cat.averageDelta === "number" ? ` (${cat.averageDelta > 0 ? "+" : ""}${cat.averageDelta})` : ""}
                    </Typography>
                  ))
                ) : (
                  <Typography fontSize={10.5} color="#9ca3af" fontStyle="italic">No parameters tagged</Typography>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
      {insufficientCategories.length > 0 && (
        <Box sx={{ mt: 1.5, border: "1px dashed #cbd5e1", borderRadius: 1, p: 1.25, bgcolor: "#f8fafc" }}>
          <Typography fontSize={10.5} fontWeight={800} color="#64748b" textTransform="uppercase" letterSpacing="0.05em" mb={0.5}>
            Insufficient Data ({insufficientCategories.length})
          </Typography>
          <Typography fontSize={10.5} color="#64748b" lineHeight={1.6}>
            {insufficientCategories.map((cat) => cat.category).join(", ")} — not enough graded calls yet to compare against the baseline.
          </Typography>
        </Box>
      )}
    </Box>
  );
}

// Baseline vs Recent Avg categorical pair (fixed order, never swapped) -
// validated colorblind-safe: `validate_palette.js "#2a78d6,#eb6834" --mode
// light` passes lightness/chroma/CVD/contrast. Blue = current/recent (matches
// this report's existing accent), orange = the older baseline value.
const IMPROVEMENT_CHART_COLORS = { recent: "#2a78d6", baseline: "#eb6834" };

const IMPROVEMENT_STATUS_COLORS = {
  "Strongly Improved": { bg: "#dcfce7", fg: "#166534" },
  Improved: { bg: "#dbeafe", fg: "#1e40af" },
  Stable: { bg: "#fef3c7", fg: "#92400e" },
  Declined: { bg: "#fee2e2", fg: "#991b1b" },
  "Insufficient Data": { bg: "#f1f5f9", fg: "#64748b" },
};

// Several parameter names carry a bracketed suffix (e.g. "Net Worth
// Calculation [CRITICAL - do not skip]") that was eating most of the axis
// label width and getting truncated mid-word. Shortened for the chart label
// only - the tooltip still shows the full original name.
function shortenParameterLabel(name = "") {
  return String(name).replace(/\s*\[[^\]]*\]\s*$/, "").trim() || name;
}

// One small chart per category (grouped/faceted rather than one 54-row
// chart): each carries its own legend + axis, so PDF pagination between
// categories never strands a chart's context on a different page the way a
// single giant chart did. `pageBreakInside: avoid` keeps a whole category's
// chart on one page.
function ImprovementCategoryChart({ category, status, averageDelta, rows }) {
  const rowHeight = 30;
  const chartHeight = Math.max(130, rows.length * rowHeight + 60);
  const statusColors = IMPROVEMENT_STATUS_COLORS[status] || IMPROVEMENT_STATUS_COLORS["Insufficient Data"];

  const option = {
    grid: { left: 210, right: 40, top: 34, bottom: 26 },
    legend: {
      top: 0,
      data: ["Baseline", "Recent Avg"],
      textStyle: { fontSize: 10.5, color: "#4b5563" },
      itemWidth: 12,
      itemHeight: 12,
    },
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "shadow" },
      formatter: (params) => {
        const row = rows[params?.[0]?.dataIndex];
        if (!row) return "";
        const sign = row.delta > 0 ? "+" : "";
        return `<b>${row.parameter}</b><br/>`
          + `Baseline: ${row.baselineRating}/5<br/>`
          + `Recent Avg: ${row.recentAverage}/5<br/>`
          + `Change: ${sign}${row.delta} (${row.status})`;
      },
    },
    xAxis: {
      type: "value",
      min: 0,
      max: 5,
      axisLabel: { fontSize: 10, color: "#657282" },
      splitLine: { lineStyle: { color: "#eef2f7" } },
    },
    yAxis: {
      type: "category",
      // Rows are sorted most-declined-first (ascending delta); ECharts'
      // default renders array index 0 at the BOTTOM of a category axis, so
      // without `inverse` the worst performers were landing at the bottom of
      // page 3 instead of the top of page 1. inverse:true puts index 0 (the
      // biggest decline) at the top, matching a "what needs attention" read.
      inverse: true,
      data: rows.map((row) => row.parameter),
      axisLabel: {
        fontSize: 10.5,
        color: "#374151",
        width: 190,
        overflow: "truncate",
        rich: { delta: { fontSize: 9.5, color: "#94a3b8" } },
        formatter: (value, index) => {
          const row = rows[index];
          const sign = row.delta > 0 ? "+" : "";
          return `${shortenParameterLabel(value)}  {delta|(${sign}${row.delta})}`;
        },
      },
      axisLine: { lineStyle: { color: "#dde3eb" } },
      axisTick: { show: false },
    },
    series: [
      {
        name: "Baseline",
        type: "bar",
        data: rows.map((row) => row.baselineRating),
        barWidth: 8,
        itemStyle: { color: IMPROVEMENT_CHART_COLORS.baseline, borderRadius: [0, 4, 4, 0] },
        label: { show: true, position: "right", fontSize: 9, color: "#657282", formatter: "{c}" },
      },
      {
        name: "Recent Avg",
        type: "bar",
        data: rows.map((row) => row.recentAverage),
        barWidth: 8,
        itemStyle: { color: IMPROVEMENT_CHART_COLORS.recent, borderRadius: [0, 4, 4, 0] },
        label: { show: true, position: "right", fontSize: 9, color: "#657282", formatter: "{c}" },
      },
    ],
  };

  return (
    <Box data-pdf-expand sx={{ border: "1px solid #dde3eb", borderRadius: 1, p: 1.5, bgcolor: "#fff", pageBreakInside: "avoid" }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
        <Typography fontSize={12} fontWeight={800} color="#202832">{category}</Typography>
        <Box component="span" sx={{
          display: "inline-block", px: 1.25, py: 0.35, borderRadius: "10px",
          bgcolor: statusColors.bg, color: statusColors.fg, fontWeight: 800, fontSize: 10, whiteSpace: "nowrap",
        }}>
          {status}{typeof averageDelta === "number" ? ` (${averageDelta > 0 ? "+" : ""}${averageDelta} avg)` : ""}
        </Box>
      </Box>
      <ReactECharts option={option} style={{ height: chartHeight, width: "100%" }} opts={{ renderer: "svg" }} />
    </Box>
  );
}

// Per-sub-parameter detail behind the 4-bucket distribution in
// OnePageImprovementSection - one baseline-vs-recent bar chart per category
// (see ImprovementCategoryChart) instead of a single monolithic chart, so
// category context and per-page legend/axis survive PDF pagination.
function ImprovementComparisonChart({ improvementSummary }) {
  const categories = improvementSummary?.categories || [];
  const insufficientRows = categories
    .flatMap((cat) => cat.subCompetencies || [])
    .filter((row) => row.status === "Insufficient Data");

  const chartableCategories = categories
    .map((cat) => ({
      ...cat,
      rows: (cat.subCompetencies || [])
        .filter((row) => typeof row.baselineRating === "number" && typeof row.recentAverage === "number")
        .sort((a, b) => (a.delta ?? 0) - (b.delta ?? 0)),
    }))
    .filter((cat) => cat.rows.length > 0);

  if (!chartableCategories.length) {
    return <Typography fontSize={13} color="#9ca3af">No improvement data available.</Typography>;
  }

  return (
    <Box>
      <Box display="flex" flexDirection="column" gap={1.5}>
        {chartableCategories.map((cat) => (
          <ImprovementCategoryChart
            key={cat.category}
            category={cat.category}
            status={cat.status}
            averageDelta={cat.averageDelta}
            rows={cat.rows}
          />
        ))}
      </Box>
      {insufficientRows.length > 0 && (
        <Box sx={{ mt: 1.5, border: "1px dashed #cbd5e1", borderRadius: 1, p: 1.25, bgcolor: "#f8fafc" }}>
          <Typography fontSize={10.5} fontWeight={800} color="#64748b" textTransform="uppercase" letterSpacing="0.05em" mb={0.5}>
            Insufficient Data ({insufficientRows.length})
          </Typography>
          <Typography fontSize={10.5} color="#64748b" lineHeight={1.6}>
            {insufficientRows.map((row) => row.parameter).join(", ")} — not enough graded calls yet to compare against the baseline.
          </Typography>
        </Box>
      )}
    </Box>
  );
}

function OnePageContextEvaluationSection({ summary }) {
  const rows = getParameterRows(summary);
  if (!rows.length) return <Typography fontSize={13} color="#9ca3af">No context evaluation data available.</Typography>;

  return (
    <TableContainer sx={{ border: "1px solid #dde3eb", borderRadius: 1, overflow: "hidden" }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: "#2d3748" }}>
            {["#", "Parameter", "Dominant Level", "Avg Rating", "General Behaviour"].map((h) => (
              <TableCell key={h} sx={{ color: "#fff", fontWeight: 800, fontSize: 11 }}>{h}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i) => (
            <TableRow key={row.parameter} sx={{ bgcolor: i % 2 === 0 ? "#fff" : "#f8fafc", pageBreakInside: "avoid" }}>
              <TableCell sx={{ fontSize: 11, fontWeight: 800, width: 42 }}>{i + 1}</TableCell>
              <TableCell sx={{ fontSize: 11, fontWeight: 800, color: "#374151", minWidth: 190 }}>{row.parameter}</TableCell>
              <TableCell><MaturityChip level={row.maturityLevel} /></TableCell>
              <TableCell sx={{ fontSize: 11, fontWeight: 800 }}>{row.averageRating ? `${row.averageRating}/5` : "N/A"}</TableCell>
              <TableCell sx={{ fontSize: 11, lineHeight: 1.55, color: "#4b5563" }}>
                {getGeneralBehaviourNote(row)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function ResistanceCoachingSection({ summary }) {
  const rows = Object.values((summary?.resistanceCoaching || []).reduce((acc, row) => {
    const key = row.category || row.parameter || "General Competency";
    if (!acc[key]) {
      acc[key] = {
        ...row,
        parameter: key,
        sellerAttempted: !!row.sellerAttempted,
      };
    } else {
      acc[key].sellerAttempted = acc[key].sellerAttempted || !!row.sellerAttempted;
      acc[key].reason = acc[key].reason || row.reason;
      acc[key].suggestedRebuttal = acc[key].suggestedRebuttal || row.suggestedRebuttal;
      acc[key].alternateApproach = acc[key].alternateApproach || row.alternateApproach;
    }
    return acc;
  }, {})).sort(sortByParameterSequence);
  if (!rows.length) {
    return <Typography fontSize={13} color="#9ca3af">No strong customer resistance or objection-handling exceptions were identified in the selected period.</Typography>;
  }
  return (
    <TableContainer sx={{ border: "1px solid #dde3eb", borderRadius: 1, overflow: "hidden" }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: "#2d3748" }}>
            {["Competency", "Seller Attempt", "Evaluation", "Suggested Rebuttal / Alternate Approach"].map((h) => (
              <TableCell key={h} sx={{ color: "#fff", fontWeight: 800, fontSize: 11 }}>{h}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i) => (
            <TableRow key={`${row.parameter}-${i}`} sx={{ bgcolor: i % 2 === 0 ? "#fff" : "#f8fafc", pageBreakInside: "avoid" }}>
              <TableCell sx={{ fontSize: 11, fontWeight: 800, color: "#374151", minWidth: 180 }}>
                {row.parameter}
              </TableCell>
              <TableCell sx={{ fontSize: 11, fontWeight: 700 }}>{row.sellerAttempted ? "Yes" : "No"}</TableCell>
              <TableCell sx={{ fontSize: 11, lineHeight: 1.55 }}>
                <MaturityChip level={row.maturityLevel} /> <br />
                {row.reason || "Resistance context captured."}
              </TableCell>
              <TableCell sx={{ fontSize: 11, lineHeight: 1.6 }}>
                {row.suggestedRebuttal || row.alternateApproach || "Coach seller to acknowledge the resistance, narrow the question, and offer a low-friction next step."}
                {row.alternateApproach && row.suggestedRebuttal ? ` ${row.alternateApproach}` : ""}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function CompetencyDetailTable({ summary }) {
  const rows = getParameterRows(summary);
  if (!rows.length) return <Typography fontSize={13} color="#9ca3af">No competency detail available.</Typography>;

  return (
    <TableContainer data-pdf-expand sx={{ overflowX: "auto", border: "1px solid #dde3eb", borderRadius: 1 }}>
      <Table size="small" sx={{ minWidth: 860 }}>
        <TableHead>
          <TableRow sx={{ bgcolor: "#2d3748" }}>
            {["Parameter", "Applicability", "Maturity", "Avg Rating", "Evidence / Coaching Need"].map((h) => (
              <TableCell key={h} sx={{ color: "#fff", fontWeight: 800, fontSize: 11 }}>{h}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i) => (
            <TableRow key={`${row.parameter}-${i}`} sx={{ bgcolor: i % 2 === 0 ? "#fff" : "#f8fafc", pageBreakInside: "avoid" }}>
              <TableCell sx={{ fontSize: 11, fontWeight: 800, color: "#374151", minWidth: 220 }}>{row.parameter}</TableCell>
              <TableCell sx={{ fontSize: 11, fontWeight: 700 }}>{row.applicability}</TableCell>
              <TableCell><MaturityChip level={row.maturityLevel} /></TableCell>
              <TableCell sx={{ fontSize: 11, fontWeight: 800 }}>{row.averageRating ? `${row.averageRating}/5` : "N/A"}</TableCell>
              <TableCell sx={{ fontSize: 11, lineHeight: 1.6, color: "#374151", minWidth: 260 }}>
                {row.evidence.slice(0, 280)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

/* ─── Agent summary card ──────────────────────────────────────── */
function AgentCard({ agent, onView }) {
  const { latestScore, bestScore, lowestScore, activeTrend } = deriveStats(agent);
  const avatarColor = getAvatarColor(agent.agentName);
  const fromDate = activeTrend[0]?.date || "";
  const toDate = activeTrend[activeTrend.length - 1]?.date || "";
  const overallScore = agent.averageCallScore ?? latestScore;
  const overallCompetency = getOverallCompetencyTag(overallScore);

  return (
    <Card variant="outlined" sx={{
      borderRadius: 3, border: "1px solid #dde3eb",
      boxShadow: "0 4px 16px rgba(22,30,42,.07)",
      display: "flex", flexDirection: "column", height: "100%",
      transition: "box-shadow 0.2s",
      "&:hover": { boxShadow: "0 8px 28px rgba(22,30,42,.14)" },
    }}>
      <CardContent sx={{ flex: 1, pb: 0 }}>
        <Box display="flex" alignItems="center" gap={1.5} mb={2}>
          <Avatar sx={{ bgcolor: avatarColor, fontWeight: 700, width: 44, height: 44, fontSize: 16 }}>
            {getInitials(agent.agentName)}
          </Avatar>
          <Box>
            <Typography fontWeight={700} fontSize={14} color="#202832">{agent.agentName}</Typography>
            <Typography fontSize={11} color="#657282">{fmtDate(fromDate)} → {fmtDate(toDate)}</Typography>
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            border: "1px solid #e2e8f0",
            borderRadius: 2,
            px: 1.25,
            py: 1,
            mb: 1.25,
            bgcolor: "#fbfdff",
          }}
        >
          <Box>
            <Typography fontSize={10.5} color="#657282" fontWeight={800} textTransform="uppercase" letterSpacing="0.07em">
              Overall Competency
            </Typography>
            <Typography fontSize={10.5} color="#64748b">
              Period avg: {overallScore.toFixed(1)}%
            </Typography>
          </Box>
          <MaturityChip level={overallCompetency} />
        </Box>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 0.5, bgcolor: "#f7f8fb", borderRadius: 2, p: 1 }}>
          {[
            { label: "Calls", value: agent.totalCalls || 0 },
            { label: "Best", value: `${bestScore.toFixed(1)}%` },
            { label: "Lowest", value: `${lowestScore.toFixed(1)}%` },
          ].map(({ label, value }) => (
            <Box key={label} textAlign="center">
              <Typography fontSize={11} color="#657282" fontWeight={700} textTransform="uppercase" letterSpacing="0.07em">{label}</Typography>
              <Typography fontWeight={800} fontSize={13} color="#202832">{value}</Typography>
            </Box>
          ))}
        </Box>
      </CardContent>
      <CardActions sx={{ px: 2, pb: 2, pt: 1.5, display: "grid", gridTemplateColumns: SHOW_IMPROVEMENT_REPORT_BUTTON ? "1fr 1fr 1fr" : "1fr 1fr", gap: 1 }}>
        <Button fullWidth variant="outlined" size="small" onClick={() => onView(agent, "onePage")}
          sx={{ textTransform: "none", fontWeight: 800, borderRadius: 2, borderColor: "#7c3aed", color: "#7c3aed", fontSize: 11.5, px: 0.5, "&:hover": { borderColor: "#6d28d9", bgcolor: "#f3e8ff" } }}>
          One Page Report
        </Button>
        {SHOW_IMPROVEMENT_REPORT_BUTTON && (
          <Button fullWidth variant="outlined" size="small" onClick={() => onView(agent, "improvement")}
            sx={{ textTransform: "none", fontWeight: 800, borderRadius: 2, borderColor: "#0a936f", color: "#0a936f", fontSize: 11.5, px: 0.5, "&:hover": { borderColor: "#087b5c", bgcolor: "#e6f7f1" } }}>
            Improvement Report
          </Button>
        )}
        <Button fullWidth variant="contained" size="small" onClick={() => onView(agent, "detailed")}
          sx={{ bgcolor: "#7c3aed", textTransform: "none", fontWeight: 800, borderRadius: 2, fontSize: 11.5, px: 0.5, "&:hover": { bgcolor: "#6d28d9" } }}>
          Detailed Report
        </Button>
      </CardActions>
    </Card>
  );
}

/* ─── Section header ──────────────────────────────────────────── */
function SectionHeader({ number, title }) {
  return (
    <Box sx={{ mb: 1.5, mt: 0.5 }}>
      <Typography fontWeight={800} fontSize={13} color="#1e40af" letterSpacing="0.01em">
        {number}.&nbsp;&nbsp;{title}
      </Typography>
      <Box sx={{ height: "2px", bgcolor: "#1e40af", mt: 0.5, borderRadius: 1 }} />
    </Box>
  );
}

/* ─── Score Progression Table (Section 1) ─────────────────────── */
function ScoreProgressionTable({ activeTrend }) {
  if (!activeTrend?.length) return null;
  const baseScore = activeTrend[0]?.averageCallScore ?? 0;
  const cols = activeTrend.map((d, i) => ({ ...d, idx: i, isBaseline: i === 0 }));

  const labelCell = (label) => (
    <TableCell sx={{
      position: "sticky", left: 0, zIndex: 2,
      bgcolor: "#f0f4f8", fontWeight: 700, fontSize: 11,
      border: "1px solid #dde3eb", minWidth: 150, py: 0.9, color: "#374151",
    }}>
      {label}
    </TableCell>
  );

  return (
    <TableContainer data-pdf-expand sx={{ overflowX: "auto", border: "1px solid #dde3eb", borderRadius: 1 }}>
      <Table size="small" sx={{ borderCollapse: "separate", borderSpacing: 0 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{
              position: "sticky", left: 0, zIndex: 3,
              bgcolor: "#f0f4f8", border: "1px solid #dde3eb", minWidth: 150,
            }} />
            {cols.map((col) => (
              <TableCell key={col.idx} align="center" sx={{
                bgcolor: col.isBaseline ? "#1e40af" : "#2d3748",
                color: "#fff", fontWeight: 700, fontSize: 10,
                border: "1px solid rgba(255,255,255,0.15)",
                minWidth: 90, whiteSpace: "nowrap", py: 0.75,
              }}>
                {fmtDate(col.date)}<br />
                <span style={{ fontWeight: 400, fontSize: 9, opacity: 0.85 }}>
                  {col.isBaseline ? "(Baseline)" : `(Call ${col.idx})`}
                </span>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            {labelCell("Score")}
            {cols.map((col) => {
              const sc = col.averageCallScore || 0;
              const color = col.isBaseline ? "#0a936f" : sc >= baseScore ? "#0a936f" : "#d92d20";
              return (
                <TableCell key={col.idx} align="center" sx={{ fontWeight: 800, fontSize: 13, color, border: "1px solid #dde3eb", bgcolor: "#fff" }}>
                  {sc.toFixed(1)}%
                </TableCell>
              );
            })}
          </TableRow>
          <TableRow>
            {labelCell("vs Baseline")}
            {cols.map((col) => {
              if (col.isBaseline) return (
                <TableCell key={col.idx} align="center" sx={{ fontWeight: 800, fontSize: 11, color: "#1e40af", border: "1px solid #dde3eb", bgcolor: "#eff6ff" }}>BASELINE</TableCell>
              );
              const delta = (col.averageCallScore || 0) - baseScore;
              const pos = delta >= 0;
              return (
                <TableCell key={col.idx} align="center" sx={{ fontSize: 11, fontWeight: 600, color: pos ? "#0a936f" : "#d92d20", border: "1px solid #dde3eb", bgcolor: "#fff" }}>
                  {pos ? "↗" : "↘"} {pos ? "+" : ""}{delta.toFixed(1)}%
                </TableCell>
              );
            })}
          </TableRow>
          <TableRow>
            {labelCell("Call-to-Call Trend")}
            {cols.map((col, ci) => {
              if (col.isBaseline) return (
                <TableCell key={col.idx} align="center" sx={{ fontSize: 11, color: "#9ca3af", border: "1px solid #dde3eb", bgcolor: "#f7f9fc" }}>—</TableCell>
              );
              const delta = (col.averageCallScore || 0) - (cols[ci - 1]?.averageCallScore || 0);
              const pos = delta >= 0;
              return (
                <TableCell key={col.idx} align="center" sx={{ fontSize: 11, fontWeight: 600, color: pos ? "#0a936f" : "#d92d20", border: "1px solid #dde3eb", bgcolor: "#f7f9fc" }}>
                  {pos ? "↗" : "↘"} {pos ? "+" : ""}{delta.toFixed(1)}%
                </TableCell>
              );
            })}
          </TableRow>
          <TableRow>
            {labelCell("Calls Evaluated")}
            {cols.map((col) => (
              <TableCell key={col.idx} align="center" sx={{ fontSize: 11, fontWeight: 600, color: "#374151", border: "1px solid #dde3eb", bgcolor: "#fff" }}>
                {col.totalCalls || 0}
              </TableCell>
            ))}
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
}

/* ─── Parameter-Wise Scores Table (Section 2) ────────────────── */
function ParameterScoresTable({ paramData, activeTrend }) {
  if (!paramData?.length || !activeTrend?.length) return null;
  const cols = activeTrend.map((d, i) => ({ ...d, idx: i, isBaseline: i === 0 }));

  return (
    <TableContainer data-pdf-expand sx={{ overflowX: "auto", border: "1px solid #dde3eb", borderRadius: 1 }}>
      <Table size="small" sx={{ borderCollapse: "separate", borderSpacing: 0 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{
              position: "sticky", left: 0, zIndex: 3,
              bgcolor: "#2d3748", color: "#fff",
              fontWeight: 700, fontSize: 11, minWidth: 200,
              border: "1px solid rgba(255,255,255,0.15)", py: 1,
            }}>
              Parameter
            </TableCell>
            {cols.map((col) => (
              <TableCell key={col.idx} align="center" sx={{
                bgcolor: col.isBaseline ? "#1e40af" : "#2d3748",
                color: "#fff", fontWeight: 700, fontSize: 10,
                minWidth: 78, whiteSpace: "nowrap",
                border: "1px solid rgba(255,255,255,0.15)", py: 0.75,
              }}>
                {fmtDate(col.date)}<br />
                <span style={{ fontWeight: 400, fontSize: 9, opacity: 0.85 }}>
                  {col.isBaseline ? "(B)" : `(${col.totalCalls || 0})`}
                </span>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {paramData.map((p, ri) => {
            const rowBg = ri % 2 === 0 ? "#fff" : "#f7f9fc";
            const bEntry = (p.trend || []).find((t) => t.date === cols[0].date);
            const bScore = bEntry && (bEntry.totalCalls || 0) > 0 ? bEntry.averageCallScore || 0 : null;
            return (
              <TableRow key={p.parameter} sx={{ pageBreakInside: "avoid" }}>
                <TableCell sx={{
                  position: "sticky", left: 0, zIndex: 2,
                  bgcolor: rowBg, fontWeight: 700, fontSize: 10.5,
                  color: "#374151", border: "1px solid #dde3eb", lineHeight: 1.35, py: 0.85,
                }}>
                  {p.parameter}
                </TableCell>
                {cols.map((col) => {
                  const entry = (p.trend || []).find((t) => t.date === col.date);
                  const score = entry && (entry.totalCalls || 0) > 0 ? entry.averageCallScore || 0 : null;
                  const isDown = score !== null && bScore !== null && !col.isBaseline && score < bScore;
                  const color = score === null ? "#9ca3af" : col.isBaseline ? "#374151" : isDown ? "#d92d20" : "#0a936f";
                  return (
                    <TableCell key={col.idx} align="center" sx={{ fontSize: 11, fontWeight: 600, color, border: "1px solid #dde3eb", bgcolor: rowBg, py: 0.75 }}>
                      {score !== null ? `${score.toFixed(1)}%` : "—"}
                    </TableCell>
                  );
                })}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

/* ─── Key Findings Section (Section 3) ───────────────────────── */
function KeyFindingsSection({ improvements, holdingSteady, targetOptimization, loading }) {
  const cols = [
    {
      key: "improvements",
      title: "IMPROVEMENTS",
      sub: "(vs Baseline)",
      headerBg: "#14532d",
      bodyBg: "#f0fdf4",
      icon: "✓",
      iconColor: "#166534",
      items: improvements || [],
    },
    {
      key: "steady",
      title: "HOLDING STEADY",
      sub: "(Comparable to Baseline)",
      headerBg: "#78350f",
      bodyBg: "#fffbeb",
      icon: "→",
      iconColor: "#78350f",
      items: holdingSteady || [],
    },
    {
      key: "target",
      title: "TARGET OPTIMIZATION AREAS",
      sub: "",
      headerBg: "#7f1d1d",
      bodyBg: "#fff1f2",
      icon: "✗",
      iconColor: "#991b1b",
      items: targetOptimization || [],
    },
  ];

  return (
    <Box sx={{
      display: "grid", gridTemplateColumns: "1fr 1fr 1fr",
      border: "1px solid #e2e8f0", borderRadius: 1, overflow: "hidden",
    }}>
      {cols.map((col, ci) => (
        <Box key={col.key} sx={{ borderRight: ci < 2 ? "1px solid #e2e8f0" : "none" }}>
          <Box sx={{ bgcolor: col.headerBg, px: 1.5, py: 1 }}>
            <Typography fontWeight={800} fontSize={11} color="#fff" letterSpacing="0.04em">
              {col.title}
            </Typography>
            {col.sub && (
              <Typography fontSize={9.5} color="rgba(255,255,255,0.75)" mt={0.25}>{col.sub}</Typography>
            )}
          </Box>
          <Box sx={{ bgcolor: col.bodyBg, p: 1.5, minHeight: 130 }}>
            {loading ? (
              <Box display="flex" justifyContent="center" pt={2}>
                <CircularProgress size={18} sx={{ color: col.headerBg }} />
              </Box>
            ) : col.items.length === 0 ? (
              <Typography fontSize={11} color="#9ca3af" fontStyle="italic">No items identified.</Typography>
            ) : (
              col.items.map((item, i) => (
                <Box key={i} display="flex" gap={0.75} mb={1} sx={{ pageBreakInside: "avoid" }}>
                  <Typography fontSize={11.5} color={col.iconColor} fontWeight={700} sx={{ flexShrink: 0 }}>
                    {col.icon}
                  </Typography>
                  <Typography fontSize={11} color="#374151" lineHeight={1.65}>
                    {typeof item === "string" ? item : item.text || item.description || ""}
                  </Typography>
                </Box>
              ))
            )}
          </Box>
        </Box>
      ))}
    </Box>
  );
}

/* ─── Priority Action Plan Table (Section 4) ─────────────────── */
function PriorityActionTable({ actions }) {
  if (!actions?.length) return null;

  const priorityBadge = (p) => {
    const label = (p || "").toLowerCase();
    if (label.includes("immediate")) return { bg: "#fee2e2", fg: "#991b1b" };
    if (label.includes("week")) return { bg: "#fef3c7", fg: "#78350f" };
    return { bg: "#f0fdf4", fg: "#14532d" };
  };

  return (
    <>
      <TableContainer sx={{ border: "1px solid #dde3eb", borderRadius: 1, overflow: "hidden" }}>
        <Table size="small" sx={{ borderCollapse: "collapse" }}>
          <TableHead>
            <TableRow sx={{ bgcolor: "#2d3748" }}>
              {["#", "Focus Area", "Action Required", "Score Impact", "Priority"].map((h, i) => (
                <TableCell key={h} sx={{
                  color: "#fff", fontWeight: 700, fontSize: 11,
                  border: "1px solid rgba(255,255,255,0.15)",
                  ...(i === 0 && { width: 36, textAlign: "center" }),
                  ...(i === 1 && { minWidth: 100 }),
                  ...(i === 3 && { minWidth: 90, textAlign: "center" }),
                  ...(i === 4 && { minWidth: 90, textAlign: "center" }),
                }}>
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {actions.map((a, i) => {
              const priorityLabel = a.priority || (i < 3 ? "Immediate" : "This Week");
              const { bg, fg } = priorityBadge(priorityLabel);
              return (
                <TableRow key={i} sx={{ bgcolor: i % 2 === 0 ? "#fff" : "#f7f9fc", pageBreakInside: "avoid" }}>
                  <TableCell sx={{ fontWeight: 700, fontSize: 11, border: "1px solid #dde3eb", textAlign: "center" }}>{i + 1}</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: 11, border: "1px solid #dde3eb" }}>{a.focusArea || `Area ${i + 1}`}</TableCell>
                  <TableCell sx={{ fontSize: 11, border: "1px solid #dde3eb", lineHeight: 1.65 }}>
                    {typeof a === "string" ? a : a.actionRequired || a.action || ""}
                  </TableCell>
                  <TableCell align="center" sx={{ fontSize: 11, border: "1px solid #dde3eb", color: "#9ca3af" }}>
                    {a.scoreImpact || "—"}
                  </TableCell>
                  <TableCell align="center" sx={{ border: "1px solid #dde3eb" }}>
                    <Box component="span" sx={{
                      display: "inline-block",
                      px: 1.5, py: 0.4,
                      borderRadius: "12px",
                      bgcolor: bg,
                      color: fg,
                      fontWeight: 700,
                      fontSize: 10,
                      lineHeight: 1.6,
                      whiteSpace: "nowrap",
                    }}>
                      {priorityLabel}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={{ bgcolor: "#f7f9fc", border: "1px solid #dde3eb", borderTop: "none", borderRadius: "0 0 4px 4px", px: 2, py: 1 }}>
        <Typography fontSize={10.5} color="#64748b" fontStyle="italic" textAlign="center">
          Implementing actions 1–{Math.min(actions.length, 4)} is projected to recover and surpass baseline in the next cycle&nbsp;|&nbsp;Report:{" "}
          {new Date().toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}
        </Typography>
      </Box>
    </>
  );
}

/* ─── Full report (forwardRef for PDF capture) ────────────────── */
const ReportContent = React.forwardRef(function ReportContent(
  { agent, token, companyId, startDate, endDate }, ref
) {
  const [competencySummary, setCompetencySummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const { activeTrend } = deriveStats(agent);
  const fromDate = activeTrend[0]?.date || startDate;
  const toDate = activeTrend[activeTrend.length - 1]?.date || endDate;
  const totalCalls = agent.totalCalls || activeTrend.reduce((s, d) => s + (d.totalCalls || 0), 0);
  const trendEndpoint = companyId === "6a6b4ce723abe63bde91abf2"
    ? apiEndPointsConfig.kiwiInteractionTrendAnalytics
    : apiEndPointsConfig.interactionTrendAnalytics;

  // Fetch parameter data
  useEffect(() => {
    setLoading(true);
    setCompetencySummary(null);
    axiosInstance
      .post(
        trendEndpoint,
        { companyId, agentId: agent.agentId, startDate, endDate },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      .then((res) => {
        const data = res?.data?.data || {};
        setCompetencySummary(data?.competencySummary ?? null);
      })
      .catch(() => {
        setCompetencySummary(null);
      })
      .finally(() => setLoading(false));
  }, [agent.agentId, startDate, endDate]); // eslint-disable-line

  return (
    <Box sx={{ bgcolor: "#dde3ea", minHeight: "100%", py: { xs: 1.5, md: 2.5 }, px: { xs: 1, md: 2 } }}>
      <Box
        ref={ref}
        sx={{ maxWidth: 1080, mx: "auto", bgcolor: "#fff", borderRadius: 1, boxShadow: "0 8px 32px rgba(0,0,0,0.12)" }}
      >
        {/* ── Banner ── */}
        <Box sx={{ bgcolor: "#2d3748", px: { xs: 2, md: 3 }, py: { xs: 1.75, md: 2.25 }, textAlign: "center", borderRadius: "4px 4px 0 0" }}>
          <Typography fontWeight={900} fontSize={{ xs: 13, md: 16 }} color="#fff" letterSpacing="0.05em">
            SKILLS COMPETENCY PERFORMANCE REPORT — {agent.agentName.toUpperCase()}
          </Typography>
          <Typography fontSize={11} color="#9ca3af" mt={0.5} letterSpacing="0.02em">
            Baseline Date: {fmtDate(fromDate)}&nbsp;&nbsp;|&nbsp;&nbsp;
            Total Calls: {totalCalls}&nbsp;&nbsp;|&nbsp;&nbsp;
            Period: {fmtDate(fromDate)} – {fmtDate(toDate)}
          </Typography>
        </Box>

        {/* ── Body ── */}
        <Box sx={{ px: { xs: 2, md: 3 }, pt: 2.5, pb: 3 }}>

          {/* Section 1 */}
          <Box mb={3.5}>
            <SectionHeader number={1} title="ONE-PAGE SKILLS COMPETENCY SUMMARY" />
            {loading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress size={28} sx={{ color: "#1e40af" }} />
              </Box>
            ) : competencySummary ? (
              <SkillsCompetencySummary summary={competencySummary} />
            ) : (
              <Typography fontSize={13} color="#9ca3af">No competency summary available.</Typography>
            )}
          </Box>

          {/* Section 2 */}
          <Box mb={3.5}>
            <SectionHeader number={2} title="COMPETENCY MATURITY LEVELS" />
            {loading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress size={28} sx={{ color: "#1e40af" }} />
              </Box>
            ) : competencySummary ? (
              <MaturityDistributionSection summary={competencySummary} />
            ) : (
              <Typography fontSize={13} color="#9ca3af">No maturity data available.</Typography>
            )}
          </Box>

          {/* Section 3 */}
          <Box mb={3.5}>
            <SectionHeader number={3} title="CONTEXT-BASED EVALUATION" />
            {loading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress size={24} sx={{ color: "#1e40af" }} />
              </Box>
            ) : competencySummary ? (
              <ContextEvaluationSection summary={competencySummary} />
            ) : (
              <Typography fontSize={13} color="#9ca3af">No context evaluation data available.</Typography>
            )}
          </Box>

          {/* Section 4 */}
          <Box mb={3.5}>
            <SectionHeader number={4} title="OBJECTION HANDLING & STRONG RESISTANCE COACHING" />
            {loading ? (
              <Box display="flex" justifyContent="center" py={4}><CircularProgress size={24} sx={{ color: "#1e40af" }} /></Box>
            ) : (
              <ResistanceCoachingSection summary={competencySummary} />
            )}
          </Box>

          {/* Section 5 */}
          <Box>
            <SectionHeader number={5} title="COMPETENCY DETAIL & DEVELOPMENT PRIORITIES" />
            {loading ? (
              <Box display="flex" justifyContent="center" py={4}><CircularProgress size={24} sx={{ color: "#1e40af" }} /></Box>
            ) : (
              <CompetencyDetailTable summary={competencySummary} />
            )}
          </Box>

        </Box>
      </Box>
    </Box>
  );
});

const OnePageReportContent = React.forwardRef(function OnePageReportContent(
  { agent, token, companyId, startDate, endDate }, ref
) {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [callTypeView, setCallTypeView] = useState("newCall");
  // null = "All calls" (the default); otherwise an explicit array of audioId
  // strings the manager checked in the "Total Calls" dropdown.
  const [selectedCallIds, setSelectedCallIds] = useState(null);

  const { activeTrend } = deriveStats(agent);
  const fromDate = activeTrend[0]?.date || startDate;
  const toDate = activeTrend[activeTrend.length - 1]?.date || endDate;
  const trendEndpoint = companyId === "6a6b4ce723abe63bde91abf2"
    ? apiEndPointsConfig.kiwiInteractionTrendAnalytics
    : apiEndPointsConfig.interactionTrendAnalytics;

  // A new date range invalidates any prior call selection - the set of calls
  // to pick from is about to change.
  useEffect(() => {
    setSelectedCallIds(null);
  }, [agent.agentId, startDate, endDate]);

  useEffect(() => {
    // Stale-response guard: clicking OK fires a new request while the
    // dialog's initial ("All calls") request - or a previous selection's
    // request - can still be in flight. Without this, whichever response
    // resolves LAST wins regardless of which one is actually current,
    // producing exactly the "sometimes shows 0, sometimes the wrong count"
    // symptom depending on network timing, not on what was actually clicked.
    let ignore = false;
    setLoading(true);
    setReportData(null);
    axiosInstance
      .post(
        trendEndpoint,
        { companyId, agentId: agent.agentId, startDate, endDate, audioIds: selectedCallIds || undefined },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      .then((res) => {
        if (ignore) return;
        setReportData(res?.data?.data || null);
      })
      .catch(() => {
        if (ignore) return;
        setReportData(null);
      })
      .finally(() => {
        if (!ignore) setLoading(false);
      });
    return () => {
      ignore = true;
    };
  }, [agent.agentId, startDate, endDate, selectedCallIds]); // eslint-disable-line

  // Only the Nippon company (fetchLastMonthInteractionTrends) returns this
  // split - Kiwi hits a different endpoint/controller entirely, so fall back
  // to the single combined summary there instead of showing an empty toggle.
  const summaryByCallType = reportData?.competencySummaryByCallType || null;
  const hasCallTypeSplit = !!summaryByCallType;

  const legacySummary = reportData?.competencySummary
    ? { ...reportData.competencySummary, overallScore: agent.averageCallScore ?? 0 }
    : null;

  const selectedSummary = hasCallTypeSplit ? (summaryByCallType[callTypeView] ?? null) : legacySummary;
  // newCallCount/followUpCallCount come from the SAME aggregation pass that
  // produces the agent card's totalCalls, so they always sum to it exactly -
  // unlike the counts inside summaryByCallType, which only count calls that
  // already have a completed QA response and can under-count a call still
  // pending QA. Used for the New Call/Follow Up toggle's own labels, which
  // always reflect the full period regardless of the call-selection below.
  const callCounts = hasCallTypeSplit
    ? {
        newCall: agent.newCallCount ?? summaryByCallType.newCall?.totalCalls ?? 0,
        followUp: agent.followUpCallCount ?? summaryByCallType.followUp?.totalCalls ?? 0
      }
    : null;
  const callTypeLabel = hasCallTypeSplit
    ? (callTypeView === "followUp" ? "Follow Up Calls" : "New Calls")
    : "All Calls";

  // The "Total Calls" tile's own list/count DOES reflect the current
  // selection - derived from agentCallList (built server-side from the same
  // adherenceRecords the summary is scored from) rather than callCounts
  // above, so the dropdown and the number it shows always agree. The
  // checklist itself (fullCallList) always spans both call types - CallSelectorTile
  // derives the tab-scoped applied/pending counts from tabCallList itself.
  const fullCallList = reportData?.agentCallList || [];
  const tabCallList = hasCallTypeSplit
    ? fullCallList.filter((c) => c.callType === (callTypeView === "followUp" ? "Follow Up Call" : "New Call"))
    : fullCallList;

  return (
    <Box sx={{ bgcolor: "#dde3ea", minHeight: "100%", py: { xs: 1.5, md: 2.5 }, px: { xs: 1, md: 2 } }}>
      <Box
        ref={ref}
        sx={{ maxWidth: 1080, mx: "auto", bgcolor: "#fff", borderRadius: 1, boxShadow: "0 8px 32px rgba(0,0,0,0.12)" }}
      >
        <Box sx={{ bgcolor: "#2d3748", px: { xs: 2, md: 3 }, py: { xs: 1.75, md: 2.25 }, textAlign: "center", borderRadius: "4px 4px 0 0" }}>
          <Typography fontWeight={900} fontSize={{ xs: 13, md: 16 }} color="#fff" letterSpacing="0.05em">
            ONE PAGE SKILLS COMPETENCY REPORT — {agent.agentName.toUpperCase()}
          </Typography>
          <Typography fontSize={11} color="#9ca3af" mt={0.5} letterSpacing="0.02em">
            Baseline Date: {fmtDate(fromDate)}&nbsp;&nbsp;|&nbsp;&nbsp;
            Period: {fmtDate(fromDate)} – {fmtDate(toDate)}
            {hasCallTypeSplit && <>&nbsp;&nbsp;|&nbsp;&nbsp;View: {callTypeLabel.toUpperCase()}</>}
          </Typography>
        </Box>

        <Box sx={{ px: { xs: 2, md: 3 }, pt: 2.5, pb: 3 }}>
          {hasCallTypeSplit && (
            <Box display="flex" justifyContent="center" mb={2.5}>
              <CallTypeToggle value={callTypeView} onChange={setCallTypeView} counts={callCounts} />
            </Box>
          )}

          <Box mb={3}>
            <SectionHeader number={1} title="SUMMARY" />
            {loading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress size={28} sx={{ color: "#1e40af" }} />
              </Box>
            ) : (
              <OnePageSummarySection
                summary={selectedSummary}
                tabCallList={tabCallList}
                fullCallList={fullCallList}
                selectedCallIds={selectedCallIds}
                onChangeSelectedCallIds={setSelectedCallIds}
              />
            )}
          </Box>

          <Box mb={3}>
            <SectionHeader number={2} title="COMPETENCY MATURITY LEVELS" />
            {loading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress size={28} sx={{ color: "#1e40af" }} />
              </Box>
            ) : (
              <OnePageMaturitySection summary={selectedSummary} />
            )}
          </Box>

          <Box>
            <SectionHeader
              number={3}
              title={hasCallTypeSplit && callTypeView === "followUp" ? "CLOSING & REFERRAL EVALUATION" : "CONTEXT-BASED EVALUATION"}
            />
            {loading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress size={24} sx={{ color: "#1e40af" }} />
              </Box>
            ) : (
              <OnePageContextEvaluationSection summary={selectedSummary} />
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
});

// Separate report, not a section inside the One Page / Detailed reports:
// shows how each parameter has trended since the agent's baseline (first-ever
// graded call) against the selected date window, via
// apiEndPointsConfig.agentCompetencyImprovement. Only meaningful for the
// Nippon legacy company - Kiwi has no equivalent endpoint/data.
const ImprovementReportContent = React.forwardRef(function ImprovementReportContent(
  { agent, token, companyId, startDate, endDate }, ref
) {
  const [improvementSummary, setImprovementSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const isKiwiCompany = companyId === "6a6b4ce723abe63bde91abf2";

  useEffect(() => {
    if (isKiwiCompany) {
      setImprovementSummary(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setImprovementSummary(null);
    axiosInstance
      .post(
        apiEndPointsConfig.agentCompetencyImprovement,
        { companyId, agentId: agent.agentId, startDate, endDate },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      .then((res) => {
        setImprovementSummary(res?.data?.data?.competencyImprovementSummary || null);
      })
      .catch(() => {
        setImprovementSummary(null);
      })
      .finally(() => setLoading(false));
  }, [agent.agentId, startDate, endDate, isKiwiCompany]); // eslint-disable-line

  return (
    <Box sx={{ bgcolor: "#dde3ea", minHeight: "100%", py: { xs: 1.5, md: 2.5 }, px: { xs: 1, md: 2 } }}>
      <Box
        ref={ref}
        sx={{ maxWidth: 1080, mx: "auto", bgcolor: "#fff", borderRadius: 1, boxShadow: "0 8px 32px rgba(0,0,0,0.12)" }}
      >
        <Box sx={{ bgcolor: "#2d3748", px: { xs: 2, md: 3 }, py: { xs: 1.75, md: 2.25 }, textAlign: "center", borderRadius: "4px 4px 0 0" }}>
          <Typography fontWeight={900} fontSize={{ xs: 13, md: 16 }} color="#fff" letterSpacing="0.05em">
            COMPETENCY IMPROVEMENT REPORT — {agent.agentName.toUpperCase()}
          </Typography>
          <Typography fontSize={11} color="#9ca3af" mt={0.5} letterSpacing="0.02em">
            Baseline: Agent's first graded call&nbsp;&nbsp;|&nbsp;&nbsp;
            Compared Against: {fmtDate(startDate)} – {fmtDate(endDate)}
          </Typography>
        </Box>

        <Box sx={{ px: { xs: 2, md: 3 }, pt: 2.5, pb: 3 }}>
          {isKiwiCompany ? (
            <Typography fontSize={13} color="#9ca3af">Improvement tracking is not available for this company.</Typography>
          ) : (
            <>
              <Box mb={3}>
                <SectionHeader number={1} title="IMPROVEMENT LEVELS" />
                <OnePageImprovementSection improvementSummary={improvementSummary} loading={loading} />
              </Box>

              <Box>
                <SectionHeader number={2} title="PARAMETER-LEVEL DETAIL" />
                {loading ? (
                  <Box display="flex" justifyContent="center" py={4}>
                    <CircularProgress size={24} sx={{ color: "#1e40af" }} />
                  </Box>
                ) : (
                  <ImprovementComparisonChart improvementSummary={improvementSummary} />
                )}
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
});

/* ─── Main export ─────────────────────────────────────────────── */
export default function AgentReports({ trendDetails, startDate, endDate, token, companyId }) {
  const [selected, setSelected] = useState(null);
  const [reportMode, setReportMode] = useState("detailed");
  const [downloading, setDownloading] = useState(false);
  // Report-local date override: lets a manager pick a different period for
  // the report dialog without touching the dashboard-wide date filter that
  // produced `startDate`/`endDate` above. Persists across report opens (via
  // CommonFilter's own localStorage), same as every other CommonFilter usage
  // in the app, so the filter button's label always matches what's applied.
  const [reportFilterPayload, setReportFilterPayload] = useState(null);
  const reportRef = useRef(null);

  const agentRows = (trendDetails?.agentTrend || []).filter((a) => (a.totalCalls || 0) > 0);
  const effectiveStartDate = reportFilterPayload?.filters?.startDate || startDate;
  const effectiveEndDate = reportFilterPayload?.filters?.endDate || endDate;

  const openReport = (agent, mode) => {
    setReportMode(mode);
    setSelected(agent);
  };

  const handleDownloadPdf = async () => {
    if (!reportRef.current || !selected) return;
    setDownloading(true);
    const restoreCallbacks = [];
    try {
      const el = reportRef.current;
      const PX_TO_MM = 25.4 / 96; // CSS px → mm at 96 dpi
      const MARGIN_MM = 6;

      // 1. Measure each scrollable table's NATURAL full width before changing anything
      const scrollers = [...el.querySelectorAll("[data-pdf-expand]")];
      const maxTablePx = scrollers.reduce((m, n) => Math.max(m, n.scrollWidth), 1080);

      // 2. Expand each table container so the table isn't clipped
      scrollers.forEach((node) => {
        const prevOverflow = node.style.overflow;
        const prevMaxWidth = node.style.maxWidth;
        restoreCallbacks.push(() => {
          node.style.overflow = prevOverflow;
          node.style.maxWidth = prevMaxWidth;
        });
        node.style.overflow = "visible";
        node.style.maxWidth = "none";
      });

      // 3. Widen the report container to exactly contain the widest table
      const savedElMaxWidth = el.style.maxWidth;
      const savedElWidth = el.style.width;
      restoreCallbacks.push(() => {
        el.style.maxWidth = savedElMaxWidth;
        el.style.width = savedElWidth;
      });
      el.style.maxWidth = "none";
      el.style.width = maxTablePx + "px";

      // 4. Wait one frame for the browser to reflow at the new width
      await new Promise((r) => setTimeout(r, 120));

      // 5. Build a page width that fits wide tables without forcing whole sections onto new pages.
      const pageWidthMm = Math.max(maxTablePx * PX_TO_MM + MARGIN_MM * 2, 297);

      await html2pdf()
        .set({
          margin: MARGIN_MM,
          filename: `${selected.agentName.replace(/\s+/g, "_")}_${
            reportMode === "onePage" ? "One_Page" : reportMode === "improvement" ? "Improvement" : "Detailed"
          }_Competency_Report.pdf`,
          image: { type: "jpeg", quality: 0.97 },
          html2canvas: {
            scale: 2,
            useCORS: true,
            allowTaint: true,
            logging: false,
            backgroundColor: "#ffffff",
            width: maxTablePx,
            windowWidth: maxTablePx,
          },
          jsPDF: { unit: "mm", format: [pageWidthMm, 210], orientation: "landscape" },
          pagebreak: {
            mode: ["css", "legacy"],
            avoid: ["tr", "[data-pdf-avoid]"],
          },
        })
        .from(el)
        .save();
    } catch (err) {
      console.error("PDF generation failed:", err);
    } finally {
      restoreCallbacks.reverse().forEach((restore) => restore());
      setDownloading(false);
    }
  };

  if (agentRows.length === 0) return null;

  return (
    <Box>
      <Typography fontWeight={700} fontSize={14} color="#464646" mb={2}>
        Agent Adherence Performance Reports
      </Typography>

      <Grid container spacing={2}>
        {agentRows.map((agent) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={agent.agentId}>
            <AgentCard agent={agent} onView={openReport} />
          </Grid>
        ))}
      </Grid>

      <Dialog
        open={!!selected}
        onClose={() => setSelected(null)}
        fullScreen
        PaperProps={{ sx: { bgcolor: "#dde3ea" } }}
      >
        <DialogTitle sx={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          bgcolor: "rgba(255,255,255,0.97)", borderBottom: "1px solid #dde3eb",
          py: 1.5, px: 3, position: "sticky", top: 0, zIndex: 10,
        }}>
          <Box>
            <Typography fontWeight={700} fontSize={15}>{selected?.agentName}</Typography>
            <Typography fontSize={12} color="#657282">
              {reportMode === "onePage"
                ? "One Page Skills Competency Report"
                : reportMode === "improvement"
                ? "Competency Improvement Report"
                : "Detailed Skills Competency Report"}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            {selected && (
              <CommonFilter
                storageName="onePageReportDateFilter"
                setPayloadData={setReportFilterPayload}
                isFilter={{}}
                defaultDateRange="month"
              />
            )}
            <Button
              size="small"
              variant="outlined"
              startIcon={downloading ? <CircularProgress size={14} /> : <FileDownloadIcon />}
              onClick={handleDownloadPdf}
              disabled={downloading}
              sx={{
                textTransform: "none", fontWeight: 700, fontSize: 12,
                borderColor: "#1e40af", color: "#1e40af",
                "&:hover": { borderColor: "#1e3a8a", bgcolor: "#eff6ff" },
              }}
            >
              {downloading ? "Generating…" : "Download PDF"}
            </Button>
            <IconButton onClick={() => setSelected(null)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          {selected && (
            reportMode === "onePage" ? (
              <OnePageReportContent
                ref={reportRef}
                agent={selected}
                token={token}
                companyId={companyId}
                startDate={effectiveStartDate}
                endDate={effectiveEndDate}
              />
            ) : reportMode === "improvement" ? (
              <ImprovementReportContent
                ref={reportRef}
                agent={selected}
                token={token}
                companyId={companyId}
                startDate={effectiveStartDate}
                endDate={effectiveEndDate}
              />
            ) : (
              <ReportContent
                ref={reportRef}
                agent={selected}
                token={token}
                companyId={companyId}
                startDate={effectiveStartDate}
                endDate={effectiveEndDate}
              />
            )
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
